import type { ParsedSqlField, SqlDataSourceField } from './types'

/** 按逗号拆分 SELECT 列表（忽略括号内逗号） */
function splitSelectList(input: string): string[] {
  const parts: string[] = []
  let current = ''
  let depth = 0

  for (const char of input) {
    if (char === '(') {
      depth += 1
      current += char
      continue
    }
    if (char === ')') {
      depth = Math.max(0, depth - 1)
      current += char
      continue
    }
    if (char === ',' && depth === 0) {
      parts.push(current.trim())
      current = ''
      continue
    }
    current += char
  }

  if (current.trim()) {
    parts.push(current.trim())
  }

  return parts
}

function normalizeIdentifier(value: string): string {
  return value.replace(/^["'`]|["'`]$/g, '')
}

function normalizeAlias(value: string): string {
  return value.trim().replace(/^["'`]|["'`]$/g, '')
}

function parseSelectColumn(expr: string): ParsedSqlField | null {
  const trimmed = expr.trim()
  if (!trimmed) return null

  const asMatch = trimmed.match(/^([\s\S]+?)\s+as\s+(.+)$/i)
  if (asMatch) {
    const alias = normalizeAlias(asMatch[2])
    if (!alias) return null
    return { field: alias, title: alias, rawExpr: trimmed }
  }

  const spaceAliasMatch = trimmed.match(/^([\s\S]+?)\s+([\w"`.]+)$/)
  if (
    spaceAliasMatch &&
    !/\)$/i.test(spaceAliasMatch[1]) &&
    !/^(count|sum|avg|min|max|case)\s/i.test(spaceAliasMatch[2])
  ) {
    const field = normalizeIdentifier(spaceAliasMatch[2].replace(/[`"]/g, ''))
    return { field, title: field, rawExpr: trimmed }
  }

  const dotMatch = trimmed.match(/\.([\w"`.]+)$/)
  if (dotMatch) {
    const field = normalizeIdentifier(dotMatch[1].replace(/[`"]/g, ''))
    return { field, title: field, rawExpr: trimmed }
  }

  const plain = normalizeIdentifier(trimmed.replace(/[`"]/g, ''))
  if (!plain || plain === '*') {
    return plain === '*' ? { field: '*', rawExpr: trimmed } : null
  }

  return { field: plain, title: plain, rawExpr: trimmed }
}

/** 从 SELECT SQL 解析输出字段（仅支持 SELECT … FROM） */
export function parseSqlSelectFields(sql: string): ParsedSqlField[] {
  const normalized = sql.replace(/\s+/g, ' ').trim()
  if (!normalized) return []

  const match = normalized.match(/^select\s+(?:distinct\s+)?(.+?)\s+from\s+/i)
  if (!match) return []

  const selectPart = match[1].trim()
  if (selectPart === '*') {
    return [{ field: '*', rawExpr: '*' }]
  }

  const columns = splitSelectList(selectPart)
  const seen = new Set<string>()
  const result: ParsedSqlField[] = []

  for (const column of columns) {
    const parsed = parseSelectColumn(column)
    if (!parsed || seen.has(parsed.field)) continue
    seen.add(parsed.field)
    result.push(parsed)
  }

  return result
}

export function inferFieldType(field: string, rawExpr: string): string {
  const expr = rawExpr.toLowerCase()
  if (
    /^(count|sum|avg|min|max)\s*\(/i.test(expr) ||
    /_qty$|_num$|_count$|length|width|amount|price|qty/i.test(field)
  ) {
    return 'number'
  }
  if (/date|time|_at$|_on$/i.test(field)) {
    return 'date'
  }
  if (/flag|enabled|is_/i.test(field)) {
    return 'switch'
  }
  return 'input'
}

export function parsedFieldsToConfig(
  parsed: ParsedSqlField[]
): SqlDataSourceField[] {
  return parsed
    .filter((item) => item.field !== '*')
    .map((item) => ({
      field: item.field,
      label: item.title ?? item.field,
      rawExpr: item.rawExpr,
      type: inferFieldType(item.field, item.rawExpr),
      enabled: true,
    }))
}
